/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import TableIcon from '@atlaskit/icon/core/table';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { PrimaryProductKeys } from '../../../../common/constants/products';
import { FlipList } from '../../../../common/ui/flip-list';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { INTENT_DETECTION, IntentMode } from '../../../../common/utils/intent-detection';
import { onIntentRouterSelected } from '../../../../common/utils/quick-find/events/intent-router-selected';
import { useQuickFindAnalytics } from '../../../../controllers/quick-find/analytics';
import { useSearchSessionId } from '../../../../controllers/store/bootstrap';
import { AllAppsIconComponent } from '../search-page-link';

import { messages } from './messages';
import { useIntentDetectionService } from './use-intent-detection-service';
import { useIntentRoutes } from './use-intent-routes';
import { useIntentRouting } from './use-intent-routing';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
});

// Minimum query length for intent detection
const MIN_QUERY_LENGTH = 2;

/**
 * Static IntentRouter rows.
 *
 * Returns two destination rows using SearchResult so they integrate
 * with @atlassian/search-dialog keyboard navigation.
 *
 */
export const IntentRouter = ({ query }: { query: string }): React.JSX.Element => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const [searchSessionId] = useSearchSessionId();

	const allAppsLabel = intl.formatMessage(messages.allApps);
	const jiraWorkItemsLabel = intl.formatMessage(messages.jiraWorkItems);

	const { defaultMode, allAppsUrl, jiraWorkItemsUrl } = useIntentRoutes({
		query,
		minQueryLength: MIN_QUERY_LENGTH,
	});

	// Use the initial primary mode as the default mode for intent detection
	const intentDetection = useIntentDetectionService({ defaultMode });

	const {
		primaryMode,
		pause,
		resume,
		pauseAndResume,
		isPaused: disabled,
	} = useIntentRouting({
		query,
		debounceMs: 250,
		intentDetection,
		initialPrimaryMode: defaultMode,
		minQueryLength: MIN_QUERY_LENGTH,
	});

	const handleResultClicked = useCallback(
		(selectedMode: IntentMode, buttonPosition: 0 | 1) => {
			const queryHash = sha256Hash(query);
			fireAnalyticsEvent(
				onIntentRouterSelected({
					attributes: {
						searchSessionId: searchSessionId || '',
						selectedMode,
						queryClassification: primaryMode,
						queryHash,
						buttonPosition,
						queryLength: query.length,
					},
				}),
			);
		},
		[query, primaryMode, searchSessionId, fireAnalyticsEvent],
	);

	// Update the global intent detection result
	INTENT_DETECTION.update({
		mode: primaryMode,
		route: primaryMode === IntentMode.FPS ? allAppsUrl : jiraWorkItemsUrl,
		query, // store the query to aid hydration
	});

	const orderedModes: IntentMode[] =
		primaryMode === IntentMode.GNIN
			? [IntentMode.GNIN, IntentMode.FPS]
			: [IntentMode.FPS, IntentMode.GNIN];

	const rowsByMode: Record<IntentMode, React.JSX.Element> = {
		[IntentMode.FPS]: (
			<SearchResult
				key="intent-all-apps"
				id="intent-all-apps"
				resultType="intentRouter"
				icon={
					fg('qf_icons_recents_suggested') ? (
						<Tile label="" size="small">
							<AiSearchIcon label={allAppsLabel} />
						</Tile>
					) : (
						<AiSearchIcon label={allAppsLabel} />
					)
				}
				title={
					<Flex direction="row" alignItems="center" gap="space.100">
						<Box>{query || intl.formatMessage(messages.searchForAllApps)}</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.allApps)}
						</Text>
						<AllAppsIconComponent product={PrimaryProductKeys.Jira} />
					</Flex>
				}
				url={allAppsUrl}
				onResultHighlighted={() => pauseAndResume()}
				onResultClicked={() =>
					handleResultClicked(IntentMode.FPS, primaryMode === IntentMode.FPS ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
		[IntentMode.GNIN]: (
			<SearchResult
				key="intent-jira-work-items"
				id="intent-jira-work-items"
				resultType="intentRouter"
				icon={
					fg('qf_icons_recents_suggested') ? (
						<Tile label="" size="small">
							<TableIcon label={jiraWorkItemsLabel} />
						</Tile>
					) : (
						<TableIcon label={jiraWorkItemsLabel} />
					)
				}
				title={
					<Flex direction="row" gap="space.100">
						<Box>{query || intl.formatMessage(messages.searchForJiraWorkItems)}</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.jiraWorkItems)}
						</Text>
					</Flex>
				}
				url={jiraWorkItemsUrl}
				onResultHighlighted={() => pauseAndResume()}
				onResultClicked={() =>
					handleResultClicked(IntentMode.GNIN, primaryMode === IntentMode.GNIN ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
	};

	const order = orderedModes.map((mode) =>
		mode === IntentMode.FPS ? 'intent-all-apps' : 'intent-jira-work-items',
	);

	return (
		<FlipList
			order={order}
			disabled={disabled}
			containerAs="div"
			containerProps={{ role: 'list' }}
			itemAs="div"
			getItemProps={() => ({
				onMouseEnter: pause,
				onMouseLeave: resume,
				onFocusCapture: pause,
				onBlurCapture: resume,
			})}
			renderItem={(key) => {
				return key === 'intent-all-apps' ? rowsByMode[IntentMode.FPS] : rowsByMode[IntentMode.GNIN];
			}}
			xcss={styles.root}
		/>
	);
};
